import { useState } from 'react';
import { Pencil, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { format } from 'date-fns';

import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { usePublishTodo, useDeleteTodo } from '@/hooks/useTodos';
import type { TodoItem } from '@/lib/types';

interface TodoItemRowProps {
  todo: TodoItem;
  onEdit: (todo: TodoItem) => void;
  /** If true, show a minimal variant (no date display). */
  compact?: boolean;
}

const priorityColors: Record<string, string> = {
  low: 'bg-blue-900/40 text-blue-300 border-blue-700/40',
  medium: 'bg-yellow-900/40 text-yellow-300 border-yellow-700/40',
  high: 'bg-red-900/40 text-red-300 border-red-700/40',
};

export function TodoItemRow({ todo, onEdit, compact = false }: TodoItemRowProps) {
  const { mutate: publishTodo } = usePublishTodo();
  const { mutate: deleteTodo, isPending: isDeleting } = useDeleteTodo();
  const [expanded, setExpanded] = useState(false);

  function handleToggle(checked: boolean) {
    publishTodo({
      id: todo.id,
      title: todo.title,
      date: todo.date,
      completed: checked,
      description: todo.description,
      priority: todo.priority,
      createdAt: todo.createdAt,
    });
  }

  function handleDelete() {
    if (confirm('Delete this to-do item?')) {
      deleteTodo(todo.id);
    }
  }

  return (
    <div
      className={cn(
        'group flex flex-col rounded-lg border border-border/60 bg-card/50 transition-all hover:border-primary/30 hover:bg-card/80',
        todo.completed && 'opacity-60',
      )}
    >
      <div className="flex items-center gap-3 px-4 py-3">
        <Checkbox
          checked={todo.completed}
          onCheckedChange={(checked) => handleToggle(checked === true)}
          className="shrink-0 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
          aria-label={`Mark "${todo.title}" as ${todo.completed ? 'incomplete' : 'complete'}`}
        />

        <div className="flex-1 min-w-0">
          <button
            type="button"
            onClick={() => todo.description && setExpanded(!expanded)}
            className={cn(
              'text-left text-sm font-medium leading-tight truncate block w-full',
              todo.completed && 'line-through text-muted-foreground',
              todo.description && 'cursor-pointer',
            )}
          >
            {todo.title}
            {todo.description && (
              <span className="inline-block ml-1 text-muted-foreground">
                {expanded ? <ChevronUp className="inline size-3" /> : <ChevronDown className="inline size-3" />}
              </span>
            )}
          </button>
          {!compact && (
            <span className="text-xs text-muted-foreground">
              {format(new Date(todo.date + 'T00:00:00'), 'MMM d, yyyy')}
            </span>
          )}
        </div>

        <Badge
          className={cn(
            'shrink-0 text-[10px] uppercase tracking-wider border',
            priorityColors[todo.priority],
          )}
        >
          {todo.priority}
        </Badge>

        <div className="flex shrink-0 gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            onClick={() => onEdit(todo)}
            aria-label="Edit todo"
          >
            <Pencil className="size-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="size-7 text-destructive hover:text-destructive"
            onClick={handleDelete}
            disabled={isDeleting}
            aria-label="Delete todo"
          >
            <Trash2 className="size-3.5" />
          </Button>
        </div>
      </div>

      {expanded && todo.description && (
        <div className="px-4 pb-3 pl-11 text-xs text-muted-foreground leading-relaxed border-t border-border/40 pt-2">
          {todo.description}
        </div>
      )}
    </div>
  );
}
